<template>
    <v-card class="defaultCard pa-3" color="bgtopology" flat>
        <!-- ENCABEZADO Y CONTROLES -->
        <v-row class="ma-0 align-center justify-space-between mb-3 ga-2">
            <v-col cols="12" sm="auto" class="pa-0 d-flex align-center ga-2">
                <span class="titlePages text-truncate">Dashboard Cementera - Vista General</span>
                <v-btn
                    icon="mdi-refresh"
                    variant="tonal"
                    color="primary"
                    size="x-small"
                    :loading="loadingRefresh"
                    @click="fetchDashboardData(true)"
                >
                    <v-icon size="16" :class="{ 'spin-icon': loadingRefresh }">mdi-refresh</v-icon>
                    <v-tooltip activator="parent" location="bottom">Actualizar datos</v-tooltip>
                </v-btn>
            </v-col>
        </v-row>

        <!-- CARGADOR GLOBAL / ALERTA DE ERROR -->
        <v-sheet v-if="showCircle" class="text-center py-8" color="transparent">
            <Prog_circular />
        </v-sheet>

        <v-row v-else-if="showError" class="ma-0 py-4 justify-center">
            <h3 class="text-grey-lighten-1">{{ t('noInformation') }}</h3>
        </v-row>

        <!-- ESTRUCTURA PRINCIPAL DEL DASHBOARD -->
        <div v-else class="d-flex flex-column ga-3 w-100">
            <!-- FILA SUPERIOR: MAPA A ANCHO COMPLETO -->
            <v-row class="ma-0 align-stretch ga-0 w-100">
                <v-col cols="12" class="pa-0">
                    <TruckMapView
                        :trucks="rawData"
                        :operability-data="getAllIEOByIP"
                        @select-device="showDialogWithType"
                    />
                </v-col>
            </v-row>

            <!-- FILA INFERIOR: RESUMEN CLIENTE -->
            <div class="w-100">
                <StatusSummaryBottomView :status-counts="statusCounts" />
            </div>
        </div>

        <!-- DIÁLOGO GLOBAL DE DETALLE -->
        <v-dialog v-model="dialog" width="85%">
            <DialogComponent
                v-if="dialog"
                :ip="selectedIp"
                :typeSel="selectedType"
                :name="selectedName"
                :model="selectedModel"
                @dialog="dialog = false"
            />
        </v-dialog>
    </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import TruckMapView from '@/components/Cementera/TruckMapView.vue';
import StatusSummaryBottomView from '@/components/Cementera/StatusSummaryBottomView.vue';
import DialogComponent from '@/components/SmartLink/dialog/dialogComponent.vue';
import Prog_circular from '@/components/PrincipalComponents/Prog_circular.vue';

import { getAllLatencyData } from '@/services/Smartlink/KPI';
import { getOperabilityLastDayTopology } from '@/services/Smartlink/Topology';
import { getAllStatusData } from '@/services/Smartlink/Principal';

import { LATENCY } from '@/interfaces/SmartLink/Topology';
import { OPERABILITY } from '@/interfaces/SmartLink/Equipos';
import { getDeviceModel, extractNumberFromName } from '@/utils/SmartLink/device';
import { getRajantGPS } from "@/services/Smartlink/Drive";
import { getAllDataLatLngSM } from "@/services/Smartlink/Map";

const { t } = useI18n();

const rawData = ref<LATENCY[]>([]);
const getAllIEOByIP = ref<OPERABILITY[]>([]);

const statusCounts = ref({
    distri: { ok: 0, alarm: 0, alert: 0, down: 0 },
    acceso: { ok: 0, alarm: 0, alert: 0, down: 0 },
    client: { ok: 0, alarm: 0, alert: 0, down: 0 }
});

const showError = ref(false);
const showCircle = ref(true);
const loadingRefresh = ref(false);
let timerId: ReturnType<typeof setInterval> | null = null;

const dialog = ref(false);
const selectedIp = ref('');
const selectedName = ref('');
const selectedType = ref('');
const selectedModel = ref('');

function showDialogWithType(item: LATENCY) {
    selectedIp.value = item.ip;
    selectedName.value = item.name;
    selectedType.value = item.tipo;
    selectedModel.value = getDeviceModel(item.anotacion);
    dialog.value = true;
}

async function fetchDashboardData(isManualRefresh = false) {
    if (isManualRefresh) loadingRefresh.value = true;

    try {
        const opRes = await getOperabilityLastDayTopology();
        if (opRes?.status === 200) getAllIEOByIP.value = opRes.data || [];

        const [responseLat, responseGps, responseSm, responseStatus] = await Promise.all([
            getAllLatencyData(),
            getRajantGPS(),
            getAllDataLatLngSM(),
            getAllStatusData()
        ]);

        if (responseStatus?.status === 200 && responseStatus.data?.countEquipData) {
            statusCounts.value = responseStatus.data.countEquipData;
        }

        showError.value = !responseLat?.showGraphs;
        showCircle.value = false;

        if (responseLat?.status === 200) {
            const latencyData = responseLat.data || [];
            const gpsRajantData = responseGps?.status === 200 ? responseGps.data : [];
            const smData = responseSm?.status === 200 ? responseSm.data : [];

            const rajantGpsMap = new Map<string, { latitud: number; longitud: number }>();
            const apMovilGpsMapById = new Map<string, { latitud: number; longitud: number }>();

            gpsRajantData.forEach((g: any) => {
                if (g.ip && g.latitud && g.longitud) {
                    rajantGpsMap.set(g.ip, { latitud: g.latitud, longitud: g.longitud });

                    const isAcceso = g.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === "acceso";
                    const isApMovil = g.tipo?.toLowerCase().replace(/\s+/g, "") === "apmovil";

                    if (isAcceso && isApMovil) {
                        const idNum = extractNumberFromName(g.name, "RAJANT");
                        if (idNum) {
                            apMovilGpsMapById.set(idNum, { latitud: g.latitud, longitud: g.longitud });
                        }
                    }
                }
            });

            const smGpsMap = new Map<string, any>();
            smData.forEach((s: any) => {
                if (s.ip) smGpsMap.set(s.ip, s);
            });

            const extractGpsIp = (anotacion: any): string => {
                if (!anotacion) return '';
                try {
                    const parsed = typeof anotacion === 'string' ? JSON.parse(anotacion) : anotacion;
                    return parsed?.gps || '';
                } catch {
                    const match = String(anotacion).match(/gps:\s*["']?([^]+)["']?/);
                    return match ? match[1].trim() : '';
                }
            };

            rawData.value = latencyData.map((item: LATENCY) => {
                let latitud = 0;
                let longitud = 0;

                const isSubtipoDistribucion = item.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === 'distribución';
                const smInfo = smGpsMap.get(item.ip);

                if (isSubtipoDistribucion || smInfo) {
                    const targetGpsIp = extractGpsIp(item.anotacion) || extractGpsIp(smInfo?.anotacion);

                    if (targetGpsIp && rajantGpsMap.has(targetGpsIp)) {
                        const linkedGps = rajantGpsMap.get(targetGpsIp)!;
                        latitud = linkedGps.latitud;
                        longitud = linkedGps.longitud;
                    } else {
                        const smIdNum = extractNumberFromName(item.name, "PMPSM");
                        if (smIdNum && apMovilGpsMapById.has(smIdNum)) {
                            const linkedGps = apMovilGpsMapById.get(smIdNum)!;
                            latitud = linkedGps.latitud;
                            longitud = linkedGps.longitud;
                        }
                    }
                } else {
                    const rajantGps = rajantGpsMap.get(item.ip);
                    if (rajantGps) {
                        latitud = rajantGps.latitud;
                        longitud = rajantGps.longitud;
                    }
                }

                const gpsInfo = gpsRajantData.find((g: any) => g.ip === item.ip);

                return {
                    ...item,
                    latitud,
                    longitud,
                    tipo: item.tipo || gpsInfo?.tipo || smInfo?.tipo || '',
                    subtipo: item.subtipo || gpsInfo?.subtipo || ''
                };
            });
        }
    } catch (err) {
        console.error("Error al refrescar datos del Dashboard:", err);
    } finally {
        loadingRefresh.value = false;
    }
}

onMounted(() => {
    fetchDashboardData();
    timerId = setInterval(() => fetchDashboardData(), 60000);
});

onUnmounted(() => {
    if (timerId) clearInterval(timerId);
});
</script>

<style scoped>
.spin-icon { 
    animation: spin 1s linear infinite; 
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>